import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/auth/consoleCapabilityContext';
import type { StorageSpace } from '@/api/console';
import {
    EmptyState,
    ErrorState,
    Input,
    PageHeader,
    SectionCard,
    SkeletonTable,
} from '@/components/ui';
import { useConsoleBreadcrumb } from '@/components/shell/ConsoleBreadcrumbContext';
import { EditQuotaModal } from './EditQuotaModal';
import { StorageSpacesTable } from './StorageSpacesTable';
import { useStorageSpaces } from './useStorageSpaces';
import styles from './StorageSpaces.module.css';

/**
 * ECR-85: `/console/storage` — Kho lưu trữ. Every Storage space across every
 * organisation, its usage against the effective quota, and (with manage) the one
 * edit this screen offers: a space's quota override.
 *
 * Data comes ONLY from portal-api's `admin/storage/spaces*` proxy — never from
 * storage-api directly. Both underlying Storage routes need
 * `storage.platform.admin`, which no person can hold (STO-PRM-17/18), so portal-api
 * calls Storage on its machine client and records the person in its audit log.
 *
 * Gating (§1b, fail-closed): `console.storage.view` → this page and its query;
 * `console.storage.manage` → the per-row edit action and the modal, which are
 * absent from the DOM without it. portal-api re-checks both.
 */
export function StoragePage() {
    const { t } = useTranslation();
    const can = useCan();
    const canView = can('console.storage.view');
    const canManage = can('console.storage.manage');
    const vm = useStorageSpaces(canView);
    const [editing, setEditing] = useState<StorageSpace | null>(null);

    useConsoleBreadcrumb([
        { label: t('console.nav.groupOperations') },
        { label: t('console.storage.title') },
    ]);

    const handleEditQuota = useCallback((space: StorageSpace) => setEditing(space), []);
    const handleCloseEdit = useCallback(() => setEditing(null), []);

    if (!canView) {
        return null;
    }

    const { spacesQuery } = vm;
    let body;
    if (spacesQuery.isLoading) {
        body = (
            <div role="status" aria-live="polite">
                <span className="portal-sr-only">{t('common.loading')}</span>
                <SkeletonTable rows={5} columns={6} />
            </div>
        );
    } else if (spacesQuery.isError) {
        body = (
            <ErrorState
                description={t('console.storage.loadError')}
                onRetry={() => void spacesQuery.refetch()}
                retrying={spacesQuery.isFetching}
            />
        );
    } else if (vm.total === 0 && !vm.searchInput.trim()) {
        body = (
            <EmptyState
                title={t('console.storage.emptyTitle')}
                description={t('console.storage.emptyDescription')}
            />
        );
    } else {
        body = (
            <div className={styles.tableRegion}>
                <StorageSpacesTable
                    spaces={vm.spaces}
                    pagination={{ page: vm.page, pageSize: vm.pageSize, total: vm.total }}
                    loading={spacesQuery.isFetching}
                    onPageChange={vm.handlePageChange}
                    onEditQuota={canManage ? handleEditQuota : undefined}
                />
            </div>
        );
    }

    return (
        <main className="portal-admin portal-console-page portal-console-page--fit">
            <PageHeader
                title={t('console.storage.title')}
                subtitle={t('console.storage.subtitle')}
            />

            <div className="fit-grow">
                <SectionCard title={t('console.storage.sectionTitle')}>
                    <div className={styles.toolbar}>
                        <Input
                            className={styles.search}
                            value={vm.searchInput}
                            onChange={(e) => vm.setSearchInput(e.target.value)}
                            placeholder={t('console.storage.searchPlaceholder')}
                            aria-label={t('console.storage.searchPlaceholder')}
                            allowClear
                        />
                    </div>
                    {body}
                </SectionCard>
            </div>

            {canManage && (
                <EditQuotaModal
                    space={editing}
                    mutation={vm.quotaMutation}
                    onClose={handleCloseEdit}
                />
            )}
        </main>
    );
}
